import { BarChart } from "@/components/charts/bar-chart";
import { LineChart } from "@/components/charts/line-chart";
import { PieChart } from "@/components/charts/pie-chart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Select } from "@/components/ui/select";
import { ApiError, api } from "@/lib/api";
import { formatFixed, formatMonthYear } from "@/lib/i18n";
import { cn, formatCurrency, formatHours } from "@/lib/utils";
import { createFileRoute, redirect } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/reports` — Analytics + Operations, the single accountant /
 * admin overview page. Non-accountants are bounced to `/my` (the
 * server also 403s them).
 *
 * Sections:
 *   - **Operations** — short-horizon company pulse: pending hours
 *     waiting for approval + the five issue / time / activity
 *     charts that used to live on the standalone `/dashboard`.
 *   - **Company health** — financial trend: revenue, tracked hours,
 *     hours mix, invoice-status mix.
 *   - **Project profitability** — margin ranking + per-project table.
 *   - **Employee profitability & utilisation** — margin ranking +
 *     utilisation + per-employee table.
 *
 * The month-range selector in the URL drives the financial queries
 * and defaults to the last six months; the Operations section is
 * fixed to its own native windows (last 7 / 14 / 30 days).
 */

const analyticsSearchSchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
});

type AnalyticsSearch = z.infer<typeof analyticsSearchSchema>;

type MonthRow = {
  label: string;
  invoiced: number;
  revenue: number;
  cost: number;
  hours: number;
  billableHours: number;
  unbilledHours: number;
  nonBillableHours: number;
};

type ProjectRow = {
  id: string;
  name: string;
  revenue: number;
  cost: number;
  margin: number;
  marginPct: number;
  hours: number;
  billableHours: number;
};

type EmployeeRow = {
  id: string;
  name: string;
  revenue: number;
  cost: number;
  margin: number;
  marginPct: number;
  hours: number;
  billableHours: number;
  nonBillableHours: number;
  utilisation: number;
};

type AnalyticsData = {
  period: { from: string; to: string };
  totals: {
    invoiced: number;
    paidInvoices: number;
    revenue: number;
    cost: number;
    margin: number;
    marginPct: number;
    billableHours: number;
    unbilledHours: number;
    nonBillableHours: number;
    trackedHours: number;
  };
  monthly: MonthRow[];
  invoiceStatusMix: Array<{ label: string; value: number }>;
  hoursMix: Array<{ label: string; value: number }>;
  projectProfit: ProjectRow[];
  employeeProfit: EmployeeRow[];
};

/** Short-horizon counts + per-day series powering the Operations
 *  section. Source: `/api/dashboard` (kept distinct from /api/reports
 *  because its windows are different — last 7 / 14 days, not a
 *  user-pickable month range). */
type ChartPoint = { label: string; value: number; color?: string };
type OperationsData = {
  pendingHours: number;
  statusPieData: ChartPoint[];
  priorityBarData: ChartPoint[];
  weekChartData: ChartPoint[];
  topProjects: ChartPoint[];
  activityChartData: ChartPoint[];
};

type LoaderData = { analytics: AnalyticsData; operations: OperationsData };

export const Route = createFileRoute("/_app/reports")({
  validateSearch: analyticsSearchSchema,
  loaderDeps: ({ search }) => ({ from: search.from, to: search.to }),
  loader: async ({ deps }): Promise<LoaderData> => {
    const params = new URLSearchParams();
    if (deps.from) params.set("from", deps.from);
    if (deps.to) params.set("to", deps.to);
    const qs = params.toString();
    try {
      const [analytics, operations] = await Promise.all([
        api.get<AnalyticsData>(qs ? `/api/reports?${qs}` : "/api/reports"),
        api.get<OperationsData>("/api/dashboard"),
      ]);
      return { analytics, operations };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: AnalyticsPage,
});

/** A list of the last 24 month keys, newest first, for the selectors. */
function monthOptions(): Array<{ value: string; label: string }> {
  const now = new Date();
  const out: Array<{ value: string; label: string }> = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: formatMonthYear(d.getFullYear(), d.getMonth(), undefined, {
        month: "short",
        year: "numeric",
      }),
    });
  }
  return out;
}

function AnalyticsPage() {
  const { t } = useTranslation();
  const { analytics: data, operations } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const months = monthOptions();

  const setRange = (next: Partial<AnalyticsSearch>) => {
    navigate({
      search: (prev) => ({ ...prev, ...next }),
    });
  };

  const revenueTrend = data.monthly.map((m) => ({ label: m.label, value: m.invoiced }));
  const trackedHoursTrend = data.monthly.map((m) => ({ label: m.label, value: m.hours }));
  const billedVsUnbilled = data.monthly.map((m) => ({
    label: m.label,
    value: m.billableHours,
  }));

  const projectMarginBar = data.projectProfit.slice(0, 12).map((p) => ({
    label: p.name,
    value: p.margin,
    color: p.margin >= 0 ? "hsl(var(--primary))" : "#ef4444",
  }));

  const revenueConcentration = data.projectProfit
    .filter((p) => p.revenue > 0)
    .slice(0, 8)
    .map((p) => ({ label: p.name, value: p.revenue }));

  const employeeRevenueBar = data.employeeProfit
    .filter((e) => e.revenue > 0 || e.cost > 0)
    .slice(0, 12)
    .map((e) => ({ label: e.name, value: e.margin }));

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t("rep.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("rep.subtitle")}</p>
        </div>
        <div className="flex items-end gap-2">
          <MonthSelect
            label={t("rep.from")}
            value={data.period.from}
            options={months}
            onChange={(v) => setRange({ from: v })}
          />
          <MonthSelect
            label={t("rep.to")}
            value={data.period.to}
            options={months}
            onChange={(v) => setRange({ to: v })}
          />
        </div>
      </div>

      {/* ---- Operations (lifted from the retired /dashboard) ------------ */}
      <Section title={t("rep.operations")}>
        <div className="grid grid-cols-1 sm:max-w-xs">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t("rep.pendingHours")}
                </p>
                <Clock className="h-4 w-4 text-accent" />
              </div>
              <p className="font-heading text-2xl font-bold mt-2">
                {formatHours(operations.pendingHours)}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <ChartCard title={t("rep.issuesByStatus")}>
            <BarChart
              data={operations.statusPieData}
              orientation="horizontal"
              showValues
              formatValue={(v) => String(v)}
            />
          </ChartCard>

          <ChartCard title={t("rep.issuesByPriority")}>
            <BarChart
              data={operations.priorityBarData}
              showValues
              orientation="vertical"
              formatValue={(v) => String(v)}
            />
          </ChartCard>

          <ChartCard title={t("rep.timeLast7")} subtitle={t("rep.hoursPerDay")}>
            <LineChart data={operations.weekChartData} showDots fill />
          </ChartCard>

          <ChartCard title={t("rep.topProjects")}>
            <BarChart
              data={operations.topProjects}
              orientation="horizontal"
              showValues
              formatValue={(v) => `${formatFixed(v, 1)}h`}
            />
            {operations.topProjects.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t("rep.noTimeThisMonth")}
              </p>
            )}
          </ChartCard>

          <Card className="lg:col-span-2">
            <CardContent className="p-5">
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                {t("rep.activity14")}
              </h3>
              <p className="text-xs text-muted-foreground mb-3">{t("rep.journalPerDay")}</p>
              <LineChart
                data={operations.activityChartData}
                showDots
                fill
                color="hsl(var(--accent))"
              />
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* ---- Headline numbers ------------------------------------------ */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label={t("rep.invoiced")} value={formatCurrency(data.totals.invoiced)} />
        <StatCard
          label={t("rep.revenueBillable")}
          value={formatCurrency(data.totals.revenue)}
          valueClass="text-green-600"
        />
        <StatCard
          label={t("rep.costRate")}
          value={formatCurrency(data.totals.cost)}
          valueClass="text-red-600"
        />
        <StatCard
          label={t("rep.margin")}
          value={`${formatCurrency(data.totals.margin)} · ${data.totals.marginPct.toFixed(0)}%`}
          valueClass={data.totals.margin >= 0 ? "text-green-600" : "text-red-600"}
        />
      </div>

      {/* ---- Company health -------------------------------------------- */}
      <Section title={t("rep.companyHealth")}>
        <div className="grid gap-6 lg:grid-cols-2">
          <ChartCard
            title={t("rep.monthlyRevenue")}
            subtitle={t("rep.invoicedThisPeriod", { amount: formatCurrency(data.totals.invoiced) })}
          >
            <BarChart
              data={revenueTrend}
              showValues={false}
              orientation="vertical"
              formatValue={(v) => formatCurrency(v)}
            />
          </ChartCard>

          <ChartCard
            title={t("rep.trackedPerMonth")}
            subtitle={t("rep.trackedTotal", { hours: formatHours(data.totals.trackedHours) })}
          >
            <LineChart data={trackedHoursTrend} formatValue={(v) => `${formatFixed(v, 0)}h`} />
          </ChartCard>

          <ChartCard title={t("rep.hoursMixTitle")} subtitle={t("rep.hoursMixSubtitle")}>
            <div className="flex flex-wrap items-center gap-6">
              <PieChart
                data={data.hoursMix}
                size={140}
                donut
                showLegend
                formatValue={formatHours}
              />
              <div className="space-y-1 text-sm">
                <MixRow label={t("rep.billable")} value={data.totals.billableHours} />
                <MixRow label={t("rep.unbilled")} value={data.totals.unbilledHours} />
                <MixRow label={t("rep.nonBillable")} value={data.totals.nonBillableHours} />
              </div>
            </div>
          </ChartCard>

          <ChartCard
            title={t("rep.invoiceMix")}
            subtitle={t("rep.paid", { amount: formatCurrency(data.totals.paidInvoices) })}
          >
            <PieChart
              data={data.invoiceStatusMix}
              size={140}
              donut
              showLegend
              formatValue={(v) => formatCurrency(v)}
            />
          </ChartCard>
        </div>

        <ChartCard title={t("rep.billableTrend")} subtitle={t("rep.monthlyBillable")}>
          <BarChart
            data={billedVsUnbilled}
            showValues={false}
            orientation="vertical"
            formatValue={(v) => `${formatFixed(v, 0)}h`}
          />
        </ChartCard>
      </Section>

      {/* ---- Project profitability ------------------------------------- */}
      <Section title={t("rep.projectProfitability")}>
        <div className="grid gap-6 lg:grid-cols-2">
          <ChartCard
            title={t("rep.projectMarginRanking")}
            subtitle={t("rep.projectMarginSubtitle")}
          >
            <BarChart
              data={projectMarginBar}
              orientation="horizontal"
              showValues
              formatValue={(v) => formatCurrency(v)}
            />
          </ChartCard>

          <ChartCard
            title={t("rep.revenueConcentration")}
            subtitle={t("rep.revenueConcentrationSubtitle")}
          >
            <PieChart
              data={revenueConcentration}
              size={150}
              donut
              showLegend
              formatValue={(v) => formatCurrency(v)}
            />
          </ChartCard>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t("rep.perProject")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.projectProfit.length === 0 ? (
              <EmptyNote />
            ) : (
              <ProjectBreakdownTable rows={data.projectProfit} />
            )}
          </CardContent>
        </Card>
      </Section>

      {/* ---- Employee profitability ------------------------------------ */}
      <Section title={t("rep.employeeProfitability")}>
        <div className="grid gap-6 lg:grid-cols-2">
          <ChartCard
            title={t("rep.employeeMarginRanking")}
            subtitle={t("rep.employeeMarginSubtitle")}
          >
            <BarChart
              data={employeeRevenueBar}
              orientation="horizontal"
              showValues
              formatValue={(v) => formatCurrency(v)}
            />
          </ChartCard>

          <ChartCard title={t("rep.utilisation")} subtitle={t("rep.utilisationSubtitle")}>
            <BarChart
              data={data.employeeProfit
                .filter((e) => e.hours > 0)
                .slice(0, 12)
                .map((e) => ({ label: e.name, value: e.utilisation }))}
              orientation="horizontal"
              showValues
              formatValue={(v) => `${v.toFixed(0)}%`}
            />
          </ChartCard>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t("rep.perEmployee")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.employeeProfit.length === 0 ? (
              <EmptyNote />
            ) : (
              <EmployeeBreakdownTable rows={data.employeeProfit} />
            )}
          </CardContent>
        </Card>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-6">
      <h2 className="font-heading text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="font-heading text-sm font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5 mb-4">{subtitle}</p>}
        {!subtitle && <div className="mb-4" />}
        {children}
      </CardContent>
    </Card>
  );
}

function StatCard({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-xl font-bold ${valueClass ?? ""}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function MixRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{formatHours(value)}</span>
    </div>
  );
}

function MonthSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <span className="flex flex-col gap-1 text-xs text-muted-foreground">
      {label}
      <Select aria-label={label} value={value} onChange={onChange} options={options} />
    </span>
  );
}

function EmptyNote() {
  const { t } = useTranslation();
  return <p className="text-sm text-muted-foreground text-center py-6">{t("rep.noDataPeriod")}</p>;
}

const projectColumns = (t: TFunction): Array<DataTableColumn<ProjectRow>> => [
  {
    key: "name",
    header: t("rep.colProject"),
    sortAccessor: (p) => p.name.toLowerCase(),
    cellClassName: "font-medium",
    cell: (p) => p.name,
  },
  {
    key: "hours",
    header: t("rep.colHours"),
    sortAccessor: (p) => p.hours,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (p) => formatHours(p.hours),
  },
  {
    key: "revenue",
    header: t("rep.colRevenue"),
    sortAccessor: (p) => p.revenue,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (p) => formatCurrency(p.revenue),
  },
  {
    key: "cost",
    header: t("rep.colCost"),
    sortAccessor: (p) => p.cost,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (p) => formatCurrency(p.cost),
  },
  {
    key: "margin",
    header: t("rep.colMargin"),
    sortAccessor: (p) => p.margin,
    headClassName: "text-right",
    cellClassName: (p) =>
      cn("text-right tabular-nums font-medium", p.margin >= 0 ? "text-green-600" : "text-red-600"),
    cell: (p) => formatCurrency(p.margin),
  },
  {
    key: "marginPct",
    header: t("rep.colMarginPct"),
    sortAccessor: (p) => p.marginPct,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums text-muted-foreground",
    cell: (p) => `${p.marginPct.toFixed(0)}%`,
  },
];

function ProjectBreakdownTable({ rows }: { rows: ProjectRow[] }) {
  const { t } = useTranslation();
  return <DataTable columns={projectColumns(t)} rows={rows} rowKey={(p) => p.id} bare />;
}

const employeeColumns = (t: TFunction): Array<DataTableColumn<EmployeeRow>> => [
  {
    key: "name",
    header: t("rep.colEmployee"),
    sortAccessor: (e) => e.name.toLowerCase(),
    cellClassName: "font-medium",
    cell: (e) => e.name,
  },
  {
    key: "hours",
    header: t("rep.colHours"),
    sortAccessor: (e) => e.hours,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (e) => formatHours(e.hours),
  },
  {
    key: "billableHours",
    header: t("rep.colBillable"),
    sortAccessor: (e) => e.billableHours,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums text-muted-foreground",
    cell: (e) => formatHours(e.billableHours),
  },
  {
    key: "revenue",
    header: t("rep.colRevenue"),
    sortAccessor: (e) => e.revenue,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (e) => formatCurrency(e.revenue),
  },
  {
    key: "cost",
    header: t("rep.colCost"),
    sortAccessor: (e) => e.cost,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (e) => formatCurrency(e.cost),
  },
  {
    key: "margin",
    header: t("rep.colMargin"),
    sortAccessor: (e) => e.margin,
    headClassName: "text-right",
    cellClassName: (e) =>
      cn("text-right tabular-nums font-medium", e.margin >= 0 ? "text-green-600" : "text-red-600"),
    cell: (e) => formatCurrency(e.margin),
  },
  {
    key: "utilisation",
    header: t("rep.colUtilisation"),
    sortAccessor: (e) => e.utilisation,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums text-muted-foreground",
    cell: (e) => `${e.utilisation.toFixed(0)}%`,
  },
];

function EmployeeBreakdownTable({ rows }: { rows: EmployeeRow[] }) {
  const { t } = useTranslation();
  return <DataTable columns={employeeColumns(t)} rows={rows} rowKey={(e) => e.id} bare />;
}
